import React, { useState } from 'react';
import { simboloMoneda } from '../services/negocioActual';
import { crearPedidoCarrito } from '../services/pedidosService';
import { avisarPedidoNuevo } from '../services/avisosPedidos';
import { origenActual } from '../utils/origenCatalogo';
import { linkWhatsAppA, linkProductoOG, linkPedido } from '../utils/compartir';
import { obtenerFotoPrincipal } from '../utils/categorias';
import { optimizarFoto } from '../utils/imagen';
import { precioEfectivo } from '../utils/precios';
import { mensajeAmigable } from '../utils/errores';
import { limpiarTelefono, telefonoValido, limpiarTexto } from '../utils/validacion';
import { totalBolsa, piezasBolsa, mensajeBolsa, nombreBonito, MAX_CANTIDAD } from '../utils/carrito';
import { normalizarEnvio, costoEnvio, resumenEnvio, ENTREGAS } from '../utils/tiendaEnvio';
import { normalizarPago, metodoPago } from '../utils/tiendaPago';
import { aplicarCombos } from '../utils/tiendaCombos';
import { useToast } from '../context/ToastContext';

// "Mi bolsa" del catálogo: la clienta revisa sus prendas, deja sus datos y
// manda TODO el pedido junto. Se guarda en Pedidos de Kontrol y después se
// abre WhatsApp con el resumen para confirmarlo con la tienda.
export default function CarritoPanel({ negocioId, negocioNombre, negocioTelefono, envio: configEnvio, pago: configPago, combos = [], lineas: lineasBase, noDisponibles = 0, onCambiar, onQuitar, onVaciar, onCerrar, onPaso, onEnviado }) {
  const { mostrarToast } = useToast();
  const [paso, setPaso] = useState('bolsa'); // bolsa → datos → listo
  const [nombre, setNombre] = useState('');
  const [telefono, setTelefono] = useState('');
  const envioCfg = normalizarEnvio(configEnvio);
  const opcionesEntrega = envioCfg.recoger ? ['domicilio', 'tienda'] : ['domicilio'];
  const [entrega, setEntrega] = useState(envioCfg.recoger ? '' : 'domicilio');
  const metodos = normalizarPago(configPago).metodos;
  const [pago, setPago] = useState(metodos.length === 1 ? metodos[0] : '');
  const [nota, setNota] = useState('');
  const [direccion, setDireccion] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(null); // copia del pedido para el WhatsApp

  const s = simboloMoneda();
  const fmt = (n) => `${s} ${Number(n || 0).toLocaleString('es-HN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  // 🎁 Combos ("3 por L 399"): mismo cálculo que hace Kontrol al guardar el pedido.
  const { lineas, ahorro, resumen: combosBolsa } = aplicarCombos(lineasBase, combos);
  const total = totalBolsa(lineas);
  const casiCombo = combosBolsa.find((r) => r.faltan > 0 && r.unidades > 0);
  const avisoCombos = (
    <>
      {ahorro > 0 && <p className="cb-combo-aviso">🎁 Combo aplicado: <b>{combosBolsa.filter((r) => r.veces > 0).map((r) => `${r.veces > 1 ? `${r.veces} × ` : ''}${r.combo.nombre}`).join(' · ')}</b> · ahorras <b>{fmt(ahorro)}</b></p>}
      {casiCombo && <p className="cb-combo-aviso">✨ Agrega {casiCombo.faltan} {casiCombo.faltan === 1 ? 'pieza más' : 'piezas más'} de {fmt(casiCombo.combo.precioPieza)} y llévate {casiCombo.combo.nombre}</p>}
    </>
  );
  const piezas = piezasBolsa(lineas);
  const envio = costoEnvio(envioCfg, entrega, total); // null = por coordinar
  const totalFinal = total + (envio || 0);

  const irADatos = () => { setPaso('datos'); onPaso && onPaso('datos', lineas, total); };

  const enviar = async () => {
    if (nombre.trim().length < 5 || !nombre.trim().includes(' ')) { mostrarToast('Escribe tu nombre completo (nombre y apellido)', 'error'); return; }
    if (!telefonoValido(telefono)) { mostrarToast('Escribe tu WhatsApp completo (8 números)', 'error'); return; }
    if (!entrega) { mostrarToast('Elige cómo quieres recibir tu pedido', 'error'); return; }
    if (entrega === 'domicilio' && direccion.trim().length < 8) { mostrarToast('Escribe tu dirección completa para el envío', 'error'); return; }
    if (metodos.length > 0 && !pago) { mostrarToast('Elige cómo vas a pagar', 'error'); return; }
    setEnviando(true);
    try {
      const r = await crearPedidoCarrito({
        negocioId, items: lineas, clienteNombre: nombre, clienteTelefono: telefono, origen: origenActual(),
        entrega, pago, nota: nota.trim(), direccion: entrega === 'domicilio' ? direccion.trim() : null,
      });
      if (r?.grupo) avisarPedidoNuevo({ grupo: r.grupo }); // suena en el celular de la tienda
      if (r?.ajustado) mostrarToast('Ajustamos alguna cantidad a las piezas que hay disponibles', 'info', 5000);
      // El envío que vale es el que calculó Kontrol (puede diferir si la tienda lo cambió).
      const envioFinal = r && Object.prototype.hasOwnProperty.call(r, 'envio') ? (r.envio === null ? null : Number(r.envio)) : envio;
      setEnviado({ lineas, total, ahorro, envio: envioFinal, grupo: r?.grupo || null, apartadoHasta: r?.apartado_hasta || null });
      setPaso('listo');
      onEnviado && onEnviado(lineas, total);
    } catch (e) {
      const m = e?.message || '';
      mostrarToast(m.includes('SIN_PRODUCTOS') ? 'Esas prendas ya no están disponibles 😔'
        : m.includes('FALTA_DIRECCION') ? 'Escribe tu dirección completa para el envío' : mensajeAmigable(e), 'error');
    } finally { setEnviando(false); }
  };

  const abrirWhatsApp = () => {
    const msg = mensajeBolsa({
      negocioNombre, lineas: enviado.lineas, total: enviado.total, ahorro: enviado.ahorro, envio: enviado.envio, nombre, telefono,
      entrega: ENTREGAS[entrega]?.replace(/^\S+\s/, ''), direccion: entrega === 'domicilio' ? direccion.trim() : '',
      pago: metodoPago(pago)?.nombre, nota: nota.trim(), simbolo: s,
      enlace: (p) => linkProductoOG({ negocioId, productoId: p.id }),
      enlacePedido: enviado.grupo ? linkPedido(enviado.grupo) : null,
    });
    window.open(linkWhatsAppA(negocioTelefono, msg), '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="cb-velo" onClick={enviando ? undefined : onCerrar}>
      <aside className="cb-bolsa" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Mi bolsa">
        <header className="cb-bolsa-cab">
          {paso === 'datos'
            ? <button className="cb-icono" onClick={() => setPaso('bolsa')} aria-label="Volver a mi bolsa">←</button>
            : <span style={{ width: 36 }} />}
          <h2 className="cb-serif">{paso === 'listo' ? '¡Pedido enviado!' : paso === 'datos' ? 'Tus datos' : 'Mi bolsa'}</h2>
          <button className="cb-icono" onClick={onCerrar} disabled={enviando} aria-label="Cerrar">✕</button>
        </header>

        {paso === 'bolsa' && (
          <>
            <div className="cb-bolsa-cuerpo">
              {noDisponibles > 0 && (
                <p className="cb-aviso">{noDisponibles === 1 ? 'Una prenda que tenías ya no está disponible y la quitamos.' : `${noDisponibles} prendas que tenías ya no están disponibles y las quitamos.`}</p>
              )}
              {lineas.length === 0 ? (
                <div className="cb-vacia">
                  <div style={{ fontSize: 40 }}>🛍️</div>
                  <p>Tu bolsa está vacía.</p>
                  <button className="cb-btn cb-btn-borde" onClick={onCerrar}>Ver productos</button>
                </div>
              ) : lineas.map((l) => {
                const p = l.producto; const foto = obtenerFotoPrincipal(p);
                const detalle = [p.talla && `Talla ${p.talla}`, p.color].filter(Boolean).join(' · ');
                return (
                  <div key={l.id} className="cb-linea">
                    {foto ? <img src={optimizarFoto(foto, 200)} alt="" /> : <div className="cb-linea-sinfoto">🛍️</div>}
                    <div className="cb-linea-info">
                      <b>{nombreBonito(p.nombre)}</b>
                      {detalle && <small>{detalle}</small>}
                      <span className="cb-linea-precio">{fmt(precioEfectivo(p))}</span>
                      {l.combo && <small className="cb-combo" style={{ margin: 0 }}>🎁 En combo {l.combo}</small>}
                      {l.max < MAX_CANTIDAD && l.cantidad >= l.max && (
                        <small className="cb-ultima">{l.max === 1 ? '✨ Pieza única' : `Solo quedan ${l.max}`}</small>
                      )}
                      <div className="cb-linea-acciones">
                        <div className="cb-cantidad" aria-label="Cantidad">
                          <button onClick={() => onCambiar(l.id, l.cantidad - 1)} disabled={l.cantidad <= 1} aria-label="Una menos">−</button>
                          <span>{l.cantidad}</span>
                          <button onClick={() => onCambiar(l.id, l.cantidad + 1, l.max)} disabled={l.cantidad >= l.max} aria-label="Una más">+</button>
                        </div>
                        <button className="cb-quitar" onClick={() => onQuitar(l.id)}>Quitar</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {lineas.length > 0 && (
              <footer className="cb-bolsa-pie">
                {avisoCombos}
                <div className="cb-total"><span>Total ({piezas} {piezas === 1 ? 'pieza' : 'piezas'})</span><b>{fmt(total)}</b></div>
                <p className="cb-letra-chica" style={{ margin: '0 0 12px', textAlign: 'left' }}>🚚 {resumenEnvio(envioCfg, fmt)}</p>
                <button className="cb-btn cb-btn-lleno" onClick={irADatos}>Hacer mi pedido</button>
                <button className="cb-enlace" onClick={onVaciar}>Vaciar bolsa</button>
              </footer>
            )}
          </>
        )}

        {paso === 'datos' && (
          <>
            <div className="cb-bolsa-cuerpo">
              <div className="cb-resumen">
                {/* Lo que va a pedir, para que lo revise antes de enviar. */}
                <div className="cb-resumen-cab">
                  <b>Tu pedido</b>
                  <button type="button" className="cb-enlace" style={{ padding: 0 }} disabled={enviando} onClick={() => setPaso('bolsa')}>Editar</button>
                </div>
                <ul className="cb-resumen-prendas">
                  {lineas.map((l) => {
                    const p = l.producto; const foto = obtenerFotoPrincipal(p);
                    const detalle = [p.talla && `Talla ${p.talla}`, p.color].filter(Boolean).join(' · ');
                    return (
                      <li key={l.id}>
                        {foto ? <img src={optimizarFoto(foto, 120)} alt="" /> : <span className="cb-resumen-sinfoto">🛍️</span>}
                        <div>
                          <b>{nombreBonito(p.nombre)}</b>
                          {detalle && <small>{detalle}</small>}
                          <small>{l.cantidad} × {fmt(precioEfectivo(p))}{l.combo ? ` · 🎁 ${l.combo}` : ''}</small>
                        </div>
                        <span>{fmt(l.subtotal)}</span>
                      </li>
                    );
                  })}
                </ul>
                {ahorro > 0 && <div className="cb-fila" style={{ color: 'var(--cb-oferta)' }}><span>🎁 Ahorro por combo</span><span>− {fmt(ahorro)}</span></div>}
                <div className="cb-fila"><span>Subtotal ({piezas} {piezas === 1 ? 'pieza' : 'piezas'})</span><span>{fmt(total)}</span></div>
                <div className="cb-fila"><span>Envío</span><span>{
                  entrega === 'tienda' ? 'Recoges en tienda'
                    : !entrega ? (envioCfg.modo === 'fijo' ? 'Elige abajo' : 'Por coordinar')
                      : envio === null ? 'Por coordinar' : envio === 0 ? 'Gratis 🎉' : fmt(envio)}</span></div>
                <div className="cb-fila cb-fila-total"><span>Total</span><b>{fmt(totalFinal)}{entrega === 'domicilio' && envio === null ? ' + envío' : ''}</b></div>
              </div>
              <label className="cb-label">Nombre completo *</label>
              <input className="cb-input" value={nombre} maxLength={80} disabled={enviando} placeholder="Ej: María Fernanda López"
                onChange={(e) => setNombre(limpiarTexto(e.target.value, 80))} autoComplete="name" />
              <label className="cb-label">Tu WhatsApp *</label>
              <input className="cb-input" type="tel" inputMode="tel" value={telefono} disabled={enviando} placeholder="Ej: 9999-9999"
                onChange={(e) => setTelefono(limpiarTelefono(e.target.value))} autoComplete="tel" />
              <label className="cb-label">¿Cómo lo quieres recibir? *</label>
              <div className="cb-opciones">
                {opcionesEntrega.map((o) => (
                  <button key={o} type="button" className={`cb-chip ${entrega === o ? 'activo' : ''}`} disabled={enviando}
                    onClick={() => setEntrega(o)}>{ENTREGAS[o]}</button>
                ))}
              </div>
              {entrega === 'domicilio' && (
                <>
                  <p className="cb-letra-chica" style={{ marginTop: 8 }}>{resumenEnvio(envioCfg, fmt)}</p>
                  <label className="cb-label">Dirección completa *</label>
                  <textarea className="cb-input" rows={3} value={direccion} maxLength={300} disabled={enviando} autoComplete="street-address"
                    onChange={(e) => setDireccion(e.target.value.slice(0, 300))} style={{ resize: 'vertical' }}
                    placeholder="Departamento, ciudad, colonia, calle, número de casa y una referencia" />
                </>
              )}
              {metodos.length > 0 && (
                <>
                  <label className="cb-label">¿Cómo vas a pagar? *</label>
                  <div className="cb-opciones">
                    {metodos.map((v) => {
                      const m = metodoPago(v);
                      return (
                        <button key={v} type="button" className={`cb-chip ${pago === v ? 'activo' : ''}`} disabled={enviando}
                          onClick={() => setPago(v)}>{m.icono} {m.nombre}</button>
                      );
                    })}
                  </div>
                  {pago === 'transferencia' && <p className="cb-letra-chica" style={{ marginTop: 8 }}>La tienda te envía los datos de la cuenta por WhatsApp.</p>}
                  {pago === 'link' && <p className="cb-letra-chica" style={{ marginTop: 8 }}>La tienda te envía por WhatsApp un link seguro para pagar con tu tarjeta de crédito o débito.</p>}
                </>
              )}
              <label className="cb-label">Nota (opcional)</label>
              <input className="cb-input" value={nota} maxLength={200} disabled={enviando} placeholder="Ej: horario para recibir, otra talla, otro color…"
                onChange={(e) => setNota(limpiarTexto(e.target.value, 200))} />
              <p className="cb-letra-chica">La tienda te confirma la disponibilidad{envio === null && entrega === 'domicilio' ? ' y el costo del envío' : ''} por WhatsApp. Todavía no pagas nada aquí.</p>
            </div>
            <footer className="cb-bolsa-pie">
              <button className="cb-btn cb-btn-lleno" onClick={enviar} disabled={enviando}>{enviando ? 'Enviando…' : `Enviar pedido · ${fmt(totalFinal)}`}</button>
            </footer>
          </>
        )}

        {paso === 'listo' && enviado && (
          <div className="cb-bolsa-cuerpo cb-listo">
            <div style={{ fontSize: 48 }}>✅</div>
            {enviado.apartadoHasta && (
              <p className="cb-aviso" style={{ textAlign: 'center' }}>
                ⏳ Te apartamos tus piezas hasta el <b>{new Date(enviado.apartadoHasta).toLocaleString('es-HN', { weekday: 'long', day: 'numeric', month: 'long', hour: 'numeric', minute: '2-digit' })}</b>. Confírmalo antes para no perderlas.
              </p>
            )}
            <p>Recibimos tu pedido de <b>{piezasBolsa(enviado.lineas)} {piezasBolsa(enviado.lineas) === 1 ? 'pieza' : 'piezas'}</b> por <b>{fmt(enviado.total + (enviado.envio || 0))}</b>{typeof enviado.envio === 'number' ? (enviado.envio > 0 ? ' con envío' : '') : entrega === 'domicilio' ? ' + envío' : ''}.</p>
            {negocioTelefono ? (
              <>
                <p className="cb-letra-chica" style={{ marginBottom: 14 }}>Un último paso: confírmalo con la tienda 👇</p>
                <button className="cb-btn cb-btn-whatsapp" onClick={abrirWhatsApp}>Confirmar por WhatsApp</button>
                <p className="cb-letra-chica">Se abre WhatsApp con tu pedido listo. Solo toca <b>enviar</b>.</p>
              </>
            ) : <p className="cb-letra-chica">La tienda te va a escribir para coordinar tu compra. 💕</p>}
            <button className="cb-btn cb-btn-borde" onClick={onCerrar}>Seguir viendo</button>
          </div>
        )}
      </aside>
    </div>
  );
}
